import { AutoComplete, AutoCompleteChangeEvent, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { GroupApi, GroupServiceSearchByPartialPolicyNumberResponse } from '../../services/api';
import { useApiConfiguration } from '../../services/api-configuration';
import { classNames } from 'primereact/utils';
import { useAppDispatch, useAppSelector } from '../../store';
import { Badge } from 'primereact/badge';
import { GroupStatus } from '../../utils/types';
import { setGlobalSearchString } from '../../store/groupList';
import { parseTimePeriodDateForUrl } from '../../utils/date';

export default function GroupSearch() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { globalSearchString } = useAppSelector((state) => state.groupList);
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [hoveredPolicyNumber, setHoveredPolicyNumber] = React.useState<string>('');
  const [searchSuggestions, setSearchSuggestions] = React.useState<GroupServiceSearchByPartialPolicyNumberResponse[]>([]);

  async function doSearch({ query }: AutoCompleteCompleteEvent) {
    setIsSearching(true);

    const api = new GroupApi(useApiConfiguration());
    const res = await api.apiGroupSearchByPartialPolicyNumberGet({
      partialPolicyNumber: query,
    });

    setSearchSuggestions(res);
    setIsSearching(false);
  }

  function selectItem({ value }: AutoCompleteSelectEvent) {
    const newPolicyNumber = (value as GroupServiceSearchByPartialPolicyNumberResponse).policyNumber!;
    const newtimePeriodDate = parseTimePeriodDateForUrl((value as GroupServiceSearchByPartialPolicyNumberResponse).timePeriodDate!);

    dispatch(setGlobalSearchString(newPolicyNumber));
    navigate(`/groups/${newtimePeriodDate}/${newPolicyNumber}`, { replace: true });
  } 

  function AutoCompleteItem(item: GroupServiceSearchByPartialPolicyNumberResponse) {
    return (
      <div className="flex items-center gap-2 p-3" onMouseEnter={() => setHoveredPolicyNumber(item.policyNumber!)} onMouseLeave={() => setHoveredPolicyNumber('')}>
        <div
          className={classNames(  
            "flex items-center justify-center w-6 h-6 rounded-full transition-colors",
            {
              "bg-grey-3": hoveredPolicyNumber !== item.policyNumber,
              "bg-white": hoveredPolicyNumber === item.policyNumber
            }
          )}>
          <span className="pi pi-search text-allstate-blue"></span>
        </div>
        <div className="flex-1">
          <div className="text-allstate-navy">{item.policyNumber}</div>
          <div className="text-2xs text-grey-8">{item.groupName}</div>
        </div>
        {item.requestStatus === GroupStatus.COMPLETED && (
          <div>
            <Badge severity="info" value="Complete" />
          </div>
        )}
      </div>
    )
  }

  function setSearchValue(v: string) {
    dispatch(setGlobalSearchString(v));
  }

  function clearSearchInput() {
    setIsSearching(false);
    dispatch(setGlobalSearchString(''));
  }

  return (
    <div className="flex flex-col gap-4 p-6 bg-white rounded">
      <h2 className="text-lg font-bold">Search group</h2>

      <div className="flex flex-col gap-0.5">
        <IconField iconPosition="left">
          <InputIcon className={`pi ${isSearching ? 'pi-spinner animate-spin' : 'pi-search'}`} />
          <AutoComplete
            value={globalSearchString}
            suggestions={searchSuggestions}
            delay={500}
            minLength={2}
            completeMethod={doSearch}
            onSelect={selectItem} 
            onChange={(e: AutoCompleteChangeEvent) => setSearchValue(e.value)}
            itemTemplate={AutoCompleteItem}
            placeholder="Enter policy number (e.g. L230266)"
            emptyMessage='No results found'
            showEmptyMessage={true}
          />

          <div
            className={classNames(
              "absolute flex items-center justify-center top-[50%] -translate-y-[50%] right-4 bg-grey-2 w-5 h-5 rounded-full font-bold",
              {
                'invisible': globalSearchString.length === 0
              }
            )}
            onClick={() => clearSearchInput()}
          >
            <span className="pi pi-times text-3xs"></span>
          </div>
        </IconField>
      </div>

    </div>
  )

}